import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Smile } from 'lucide-react';

interface EmojiPickerProps {
  value: string;
  onChange: (emoji: string) => void;
}

// Curated list of popular emojis for marketplace categories
const EMOJI_CATEGORIES = {
  'Popular': ['💻', '🚗', '🏡', '👔', '⚽', '👶', '📚', '🐾', '🎮', '🎨', '🍔', '💄'],
  'Electronics': ['💻', '📱', '⌚', '🎧', '📷', '🖥️', '⌨️', '🖱️', '🎮', '📺', '📻', '💡'],
  'Vehicles': ['🚗', '🏍️', '🚙', '🚕', '🚌', '🚐', '🚚', '🚛', '🚜', '🛵', '🚲', '⛵'],
  'Home & Lifestyle': ['🏡', '🛋️', '🪑', '🛏️', '🚪', '🪟', '🌿', '🌱', '🌺', '🏠', '🔨', '🔧'],
  'Fashion': ['👔', '👗', '👠', '👟', '👜', '💍', '👑', '🎩', '🧢', '👓', '🕶️', '💄'],
  'Sports': ['⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏓', '🏸', '🏒', '⛳', '🎣', '🏊'],
  'Baby & Kids': ['👶', '🍼', '🧸', '🎈', '🎁', '🎂', '👼', '🧷', '🍭', '🎨', '✏️', '📚'],
  'Food': ['🍔', '🍕', '🍣', '🍰', '☕', '🍷', '🍎', '🥗', '🍜', '🍪', '🥤', '🧁'],
  'Pets': ['🐾', '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁'],
  'Objects': ['📦', '📋', '📌', '📍', '🔑', '🔒', '🎁', '🎀', '🏷️', '💰', '💳', '📧'],
  'Symbols': ['⭐', '❤️', '💚', '💙', '💜', '🔥', '✨', '💫', '🌟', '🎯', '🏆', '🎖️'],
};

export function EmojiPicker({ value, onChange }: EmojiPickerProps) {
  const [open, setOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Popular');

  const handleEmojiSelect = (emoji: string) => {
    onChange(emoji);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start text-left font-normal"
        >
          {value ? (
            <span className="text-2xl mr-2">{value}</span>
          ) : (
            <Smile className="mr-2 h-4 w-4" />
          )}
          <span className="text-muted-foreground">
            {value ? 'Change emoji' : 'Pick an emoji'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="flex flex-col h-96">
          {/* Category Tabs */}
          <div className="flex overflow-x-auto border-b p-1 gap-1">
            {Object.keys(EMOJI_CATEGORIES).map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1.5 text-xs whitespace-nowrap rounded transition-colors ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-secondary'
                }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Emoji Grid */}
          <div className="flex-1 overflow-y-auto p-2">
            <div className="grid grid-cols-6 gap-1">
              {EMOJI_CATEGORIES[selectedCategory as keyof typeof EMOJI_CATEGORIES].map((emoji, index) => (
                <button
                  key={`${emoji}-${index}`}
                  onClick={() => handleEmojiSelect(emoji)}
                  className="w-full aspect-square flex items-center justify-center text-2xl hover:bg-secondary rounded transition-colors"
                  title={emoji}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t p-2 text-xs text-muted-foreground text-center">
            Click an emoji to select it
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
